<template>
  <div class="grid mt-5">
    <Card class="col-12 col-lg-6">
      <template #header><h5>Reset All Items</h5></template>
      <template #content>
        <div class="reset-all-items mt-4">
          <p>
            This action will reset all items in the auction. All bids or
            purchases will be deleted, and item statuses will be reset.
          </p>
          <argon-button
            type="button"
            class="ms-auto btn-danger"
            @click="resetAllItems"
          >
            Reset All Items
          </argon-button>
        </div></template
      >
    </Card>
  </div>
</template>

<script>
import {
  collection,
  collectionGroup,
  getDocs,
  doc,
  writeBatch,
  deleteField,
} from "firebase/firestore";
import { db } from "@/firebase";
import { useToast } from "vue-toastification";
import ArgonButton from "@/components/ArgonButton.vue"; // Assuming ArgonButton is a reusable button component

// Firestore batch limit
const BATCH_LIMIT = 450; // leave a little headroom under the 500-op cap

// Commit a list of refs to delete in chunks of BATCH_LIMIT
const deleteRefsInChunks = async (refs) => {
  let deleted = 0;
  for (let i = 0; i < refs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    const slice = refs.slice(i, i + BATCH_LIMIT);
    slice.forEach((ref) => batch.delete(ref));
    await batch.commit();
    deleted += slice.length;
  }
  return deleted;
};

// Reset item documents in chunks too (to stay under 500 ops per commit)
const resetItemsInChunks = async (itemDocs) => {
  let reset = 0;
  for (let i = 0; i < itemDocs.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    const slice = itemDocs.slice(i, i + BATCH_LIMIT);
    slice.forEach((itemDoc) => {
      const itemRef = doc(db, "auctionItems", itemDoc.id);
      batch.update(itemRef, {
        latestBidder: deleteField(),
        latestBidderID: deleteField(),
        latestBid: deleteField(),
        winnerSMSSent: false,
        bidCount: 0,
      });
    });
    await batch.commit();
    reset += slice.length;
  }
  return reset;
};

export default {
  name: "ResetAllItems",
  components: {
    ArgonButton,
  },
  setup() {
    const toast = useToast();

    const resetAllItems = async () => {
      if (
        !confirm(
          "Are you sure you want to reset all items? This action cannot be undone."
        )
      ) {
        return;
      }

      try {
        // 1. Gather all items (to reset their fields)
        const auctionItemsSnapshot = await getDocs(
          collection(db, "auctionItems")
        );

        // 2. Gather ALL bids across ALL subcollections (handles orphans from
        //    deleted items too — `collectionGroup` queries every subcollection
        //    named "bids" anywhere in the database).
        const allBidsSnapshot = await getDocs(collectionGroup(db, "bids"));
        const allPurchasesSnapshot = await getDocs(
          collectionGroup(db, "purchase")
        );

        const bidRefs = allBidsSnapshot.docs.map((d) => d.ref);
        const purchaseRefs = allPurchasesSnapshot.docs.map((d) => d.ref);

        // 3. Delete in chunks to stay under Firestore's 500-op batch cap
        const deletedBids = await deleteRefsInChunks(bidRefs);
        const deletedPurchases = await deleteRefsInChunks(purchaseRefs);
        const resetItems = await resetItemsInChunks(auctionItemsSnapshot.docs);

        toast.success(
          `Reset ${resetItems} items, deleted ${deletedBids} bids and ${deletedPurchases} purchases.`
        );
      } catch (error) {
        console.error("Error resetting all items:", error);
        toast.error("Failed to reset all items.");
      }
    };

    return {
      resetAllItems,
    };
  },
};
</script>

<style scoped>
.reset-all-items {
  padding: 20px;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 5px;
}
</style>
